import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { PremiumFooter } from '../src/components/landing/PremiumFooter';
import { Container } from '../src/components/primitives/Container';
import { Text } from '../src/components/primitives/Text';
import { useTranslation } from '../src/contexts/TranslationContext';
import { colors } from '../src/theme/colors';
import { spacing } from '../src/theme/spacing';

const LAST_UPDATED = 'July 27, 2026';

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <View style={styles.section}>
    <Text variant="h4" style={styles.sectionTitle}>
      {title}
    </Text>
    {children}
  </View>
);

const Paragraph = ({ children }: { children: ReactNode }) => (
  <Text variant="body" color={colors.text.secondary} style={styles.paragraph}>
    {children}
  </Text>
);

const BulletItem = ({ children }: { children: ReactNode }) => (
  <View style={styles.bulletRow}>
    <Text variant="body" color={colors.primary[500]} style={styles.bullet}>
      {'•'}
    </Text>
    <Text variant="body" color={colors.text.secondary} style={styles.bulletText}>
      {children}
    </Text>
  </View>
);

export default function RefundPolicyPage() {
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          testID="refunds-back-button"
          onPress={() => router.push('/landing')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('common.actions.goBack')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('common.actions.goBack')}
          </Text>
        </Pressable>

        <View style={styles.header}>
          <Text testID="refunds-heading" variant="h1" style={styles.title}>
            Refund Policy
          </Text>
          <Text variant="bodySmall" color={colors.text.tertiary}>
            Last updated: {LAST_UPDATED}
          </Text>
        </View>

        <View style={styles.divider} />

        <Section title="1. Overview">
          <Paragraph>
            This Refund Policy explains when and how you can request a refund for paid subscriptions
            to Funnel Panel (the "Service"), operated by FLOWSOFT S.A.S ("we," "our," or "us"). It
            applies alongside our Terms of Service.
          </Paragraph>
          <Paragraph>
            Wompi purchases provide one-time prepaid access periods. This policy applies to those
            purchases and prior payments; use the support path below for any refund request.
          </Paragraph>
        </Section>

        <Section title="2. 30-Day Money-Back Guarantee">
          <Paragraph>
            We offer a 30-day money-back guarantee on the first payment for any new paid
            subscription. If you are not satisfied for any reason, you may request a full refund
            within 30 days of your initial purchase, and we will refund that payment in full. You do
            not need to provide a reason.
          </Paragraph>
          <Paragraph>
            The 30-day guarantee applies only to the initial one-time purchase; purchases do not
            renew automatically.
          </Paragraph>
        </Section>

        <Section title="3. Access Periods and Cancellation">
          <Paragraph>
            Each Wompi checkout buys one prepaid monthly or annual access period. It does not renew
            automatically, so no cancellation action is required to prevent another charge.
          </Paragraph>
          <BulletItem>
            Paid access remains available until the end of the one-time access period you have
            already paid for. We do not automatically pro-rate or refund the remainder of a billing
            period.
          </BulletItem>
          <BulletItem>
            Because access does not renew automatically, there is no future renewal charge to avoid.
          </BulletItem>
          <BulletItem>
            If a charge was made in error, or you believe you are entitled to a refund under
            applicable consumer protection law, contact us and we will review your request in good
            faith.
          </BulletItem>
        </Section>

        <Section title="4. Free Plan and Trials">
          <Paragraph>
            Our free plan and any free trial let you evaluate the Service before you pay. No charge
            is made for free usage, so there is nothing to refund for the free plan. If a trial
            converts into a paid subscription, the 30-day money-back guarantee in Section 2 applies
            to your first payment.
          </Paragraph>
        </Section>

        <Section title="5. Usage-Based and Consumed Services">
          <Paragraph>
            Some features are metered and consume credits or allowances (for example, enrichment
            lookups, voice calls and minutes, and messaging). Where a refund is issued for a billing
            period, we may deduct the value of metered services and third-party costs you have
            already consumed during that period, to the extent permitted by applicable law and the
            terms presented with the original purchase.
          </Paragraph>
        </Section>

        <Section title="6. How to Request a Refund">
          <Paragraph>You can request a refund through us:</Paragraph>
          <BulletItem>
            Email contact@flowsoftdev.com from the address associated with your account and include
            your order or invoice number.
          </BulletItem>
        </Section>

        <Section title="7. Processing and Timing">
          <Paragraph>
            Approved refunds are returned to the original payment method used at checkout.
            Processing time depends on the provider used for the original transaction and your
            financial institution, and may take several business days.
          </Paragraph>
        </Section>

        <Section title="8. Chargebacks">
          <Paragraph>
            If you have a billing concern, please contact us first so we can resolve it quickly.
            Initiating a chargeback with your bank or card issuer before contacting us may result in
            suspension of your account while the dispute is investigated.
          </Paragraph>
        </Section>

        <Section title="9. Changes to This Policy">
          <Paragraph>
            We may update this Refund Policy from time to time. We will post the updated policy on
            this page and revise the "Last updated" date. Changes do not affect refund requests that
            were already submitted before the change took effect.
          </Paragraph>
        </Section>

        <Section title="10. Contact Us">
          <Paragraph>
            If you have any questions about this Refund Policy, please contact us at:
          </Paragraph>
          <BulletItem>Email: contact@flowsoftdev.com</BulletItem>
          <BulletItem>Company: FLOWSOFT S.A.S</BulletItem>
        </Section>
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  header: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 32 }),
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  section: {
    marginBottom: spacing.xxl,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: spacing.md,
    color: colors.text.primary,
  },
  paragraph: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
    marginBottom: spacing.md,
  },
  bulletRow: {
    flexDirection: 'row',
    paddingLeft: spacing.md,
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  bullet: {
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
  bulletText: {
    flex: 1,
    lineHeight: Platform.select({ web: 26, default: 24 }),
  },
});
